/* global process, Iterate, angular */
import { ResourceService } from "../common/common.services";
import _ from "lodash";
import { saveAs } from "file-saver";
import promisePoller, { CANCEL_TOKEN } from "promise-poller";
import mixpanel from "mixpanel-browser";
import { ENGLISH, SID_LOOKUP } from "../../app.constants";
import { publicIpv4 } from "public-ip";
import { downloadFile, getCloudinaryUrl } from "../common/common.utils";
import { compileSchema, draft07 } from "json-schema-library";
import { addFormats } from "json-schema-library/formats";
import {
    ACTIVE_GROWER_ADMIN,
    AUTHENTICATED_PERMISSION,
    PERMISSION_NAMES,
} from "../auth/auth.constants";
import { Client as ConversationsClient } from "@twilio/conversations";
import {
    setUser,
    captureMessage,
    captureException,
    addBreadcrumb,
} from "@sentry/browser";
import { searchClient } from "@algolia/client-search";
import { liteClient as algoliasearch } from "algoliasearch/lite";

import { UNDERSCORE_DATE_FORMAT } from "../paperwork/constants";
import {
    buildRoleScopedFormSchema,
    getDocumentFieldPathData,
    getReactSchemaFormData,
    getSchemaDataValue,
    getSignatureFieldNames,
    setSchemaDataValue,
} from "../paperwork/document_field_path.utils";
import moment from "moment";
import { CLOSED } from "../communication/constants";
import {
    ACCIDENT_REPORT_SOFT_REQUIRED_FIELDS,
    CANCELLED,
    COMPLETE,
} from "../safety/safety.constants";
import { CHARGE_AUTOMATICALLY } from "../common/common.constants";
import { SIGNER } from "../paperwork/constants";
import { SPRAY_APPLICATION_RECORD_STATUSES } from "../spray/constants";

addFormats([draft07]);

function addTwilioConversationsBreadcrumb(eventName, data) {
    addBreadcrumb({
        category: "twilio.conversations",
        message: eventName,
        data,
    });
}

export class GroupService extends ResourceService {
    /* @ngInject */
    constructor(
        Restangular,
        $q,
        MembershipService,
        GroupManagementMembershipService,
    ) {
        super(Restangular, $q, "groups");
        this.MembershipService = MembershipService;
        this.GroupManagementMembershipService =
            GroupManagementMembershipService;
        let ctrl = this;

        this.Restangular.extendModel(this.resource, function (model) {
            model.addMember = function (growerMembership) {
                return ctrl.MembershipService.create({
                    group: this.sid,
                    user: growerMembership.user,
                    grower_membership: growerMembership.sid,
                }).then(
                    function (membership) {
                        return membership;
                    },
                    function (reason) {
                        return reason.data;
                    },
                );
            };

            model.addManager = function (user, permissions) {
                return ctrl.GroupManagementMembershipService.create({
                    user: user,
                    group: this.sid,
                    active: true,
                    permissions: permissions,
                });
            };

            return model;
        });
    }
}

export class MembershipService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "memberships");

        this.Restangular.extendModel(this.resource, function (model) {
            model.end = function () {
                this.active = false;
                this.end_date = moment();
                return this.save();
            };

            return model;
        });
    }
}

export class GroupManagementMembershipService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "groupmanagementmemberships");
    }
}

export class GroupManagementPermissionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "groupmanagementpermissions");
    }
}

export class SyncRecordService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "syncrecords");
    }
}

export class FarmService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, ManagementMembershipService) {
        super(Restangular, $q, "farms");

        let ctrl = this;
        this.ManagementMembershipService = ManagementMembershipService;

        this.Restangular.extendModel(this.resource, function (model) {
            model.addManager = function (user, permissions) {
                return ctrl.ManagementMembershipService.create({
                    user: user,
                    farm: this.sid,
                    active: true,
                    permissions: permissions,
                });
            };

            return model;
        });
    }
}

export class FieldService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "fields");
    }

    uploadFields(uploadProperties) {
        let formData = new FormData();
        formData.append("file", uploadProperties.file);
        formData.append("farm", uploadProperties.farm);
        return this.Restangular.all(this.resource).customPOST(
            formData,
            "upload_fields",
            undefined,
            {
                "Content-Type": undefined,
            },
        );
    }
}

export class CropService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "crops");
    }
}

export class ManagementPermissionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "managementpermissions");
    }
}

export class ManagementMembershipService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "managementmemberships");
    }
}

export class GrowerManagementMembershipService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "growermanagementmemberships");
    }
}

export class GrowerManagementPermissionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "growermanagementpermissions");
    }
}

const ALGOLIA_KEY_REFRESH_BUFFER_SECONDS = 30;

export class GrowerMembershipService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "growermemberships");
        this.$http = $http;

        this.Restangular.extendModel(this.resource, function (model) {
            model.end = function () {
                this.active = false;
                this.end_date = moment();
                return this.save();
            };

            model.sendSelfieLink = function () {
                return this.customGET("sendselfielink");
            };

            model.sendHandbook = function () {
                return this.customGET("sendhandbook");
            };

            model.sendOwnDocumentsLink = function () {
                return this.customGET("sendowndocumentslink");
            };

            model.clearPhoto = function () {
                return this.customPOST(undefined, "clear_photo");
            };

            return model;
        });
    }

    new(params) {
        return this.Restangular.all(this.resource).customPOST(params, "new");
    }

    sendPhoneOwnershipVerificationCode(params) {
        return this.$http.post(
            `${process.env.API_DOMAIN}/api/auth/phone-transfer/request/`,
            params,
        );
    }

    verifyPhoneOwnershipCode(params) {
        return this.$http.post(
            `${process.env.API_DOMAIN}/api/auth/phone-transfer/verify/`,
            params,
        );
    }

    getAlgoliaKey() {
        return this.Restangular.all(this.resource).customGET("algolia");
    }

    search(query, filters, additionalParams = {}) {
        let svc = this;
        return svc.getSearchClient().then(function (searchClient) {
            let indexName =
                additionalParams.indexName || "grower_membership_index";
            let searchParams = {
                filters,
                query,
                ..._.omit(additionalParams, "indexName"),
            };
            return svc.$q.when(
                searchClient.searchSingleIndex({
                    indexName,
                    searchParams,
                }),
            );
        });
    }

    getSearchClient() {
        let ctrl = this;
        if (
            ctrl.searchClient &&
            moment().unix() + ALGOLIA_KEY_REFRESH_BUFFER_SECONDS <
                ctrl.algoliaKeyValidUntil
        ) {
            return ctrl.$q.resolve(ctrl.searchClient);
        }
        if (ctrl.refreshingSearchClientPromise) {
            return ctrl.refreshingSearchClientPromise;
        }
        ctrl.refreshingSearchClientPromise = ctrl
            .getAlgoliaKey()
            .then(function (algoliaParams) {
                ctrl.searchClient = searchClient(
                    process.env.ALGOLIA_APP_ID,
                    algoliaParams.public_key,
                );
                ctrl.algoliaKeyValidUntil = algoliaParams.valid_until;
                return ctrl.searchClient;
            })
            .finally(function () {
                ctrl.refreshingSearchClientPromise = undefined;
            });
        return ctrl.refreshingSearchClientPromise;
    }

    sendOwnDocumentsLink(sid) {
        return this.Restangular.one(this.resource, sid).customGET(
            "sendowndocumentslink",
        );
    }

    sync(syncProperties) {
        let svc = this;
        let fd = new FormData();
        fd.append("file", syncProperties.file);
        fd.append("grower", syncProperties.grower);
        fd.append("upload_notes", syncProperties.upload_notes);
        return svc.$http.post(
            _.join([process.env.API_DOMAIN, "api", svc.resource, "sync"], "/"),
            fd,
            {
                headers: { "Content-Type": undefined },
                transformRequest: angular.identity,
            },
        );
    }
}

export class ExportFormatService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "exportformats");
    }
}
export class NoticeSubscriptionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "noticesubscriptions");

        let ctrl = this;
        ctrl.Restangular.extendModel(this.resource, function (model) {
            model.unsubscribe = function () {
                this.active = false;
                return this.save();
            };

            return model;
        });
    }
}

export class ThemeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "themes");
    }
}

export class TradeOrganizationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "tradeorganizations");
    }
}

export class SubjectService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "subjects");
    }
}

export class DocumentPackageTagService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "documentpackagetags");
    }
}

export class FeatureRequestService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "featurerequests");
    }
}

export class SDSProductService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "sdsproducts");
    }
}

export class SDSProductInventoryItemService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "sdsproductinventoryitems");
    }
}

export class SDSLookupRequestService {
    /* @ngInject */
    constructor($http) {
        this.$http = $http;
        this.endpoint = _.join(
            [process.env.API_DOMAIN, "api", "sdslookuprequests"],
            "/",
        );
        this.requestConfig = {
            skipAuthorization: true,
        };
    }

    create(data) {
        let svc = this;
        return svc.$http
            .post(svc.endpoint, data, svc.requestConfig)
            .then((response) => response.data);
    }

    retrieve(sid, params = {}) {
        let svc = this;
        return svc.$http
            .get(svc.endpoint + "/" + sid, {
                ...svc.requestConfig,
                params,
            })
            .then((response) => response.data);
    }
}

export class AccountService extends ResourceService {
    /* @ngInject */
    constructor(
        Restangular,
        $q,
        PermRoleStore,
        PermPermissionStore,
        $translate,
        amMoment,
        AuthService,
    ) {
        super(Restangular, $q, "users");
        let ctrl = this;
        ctrl.PermPermissionStore = PermPermissionStore;
        ctrl.AuthService = AuthService;

        ctrl.Restangular.extendModel(this.resource, function (model) {
            model.getDisplayName = function () {
                return this.mobile || this.email || this.sid;
            };

            model.updateMixpanel = function () {
                mixpanel.identify(this.sid);
                let mixpanelPeopleProperties = {
                    $email: this.email,
                    $phone: this.mobile,
                    preferred_language: this.preferred_language,
                    $name: this.getDisplayName(),
                };
                mixpanel.people.set(mixpanelPeopleProperties);
                mixpanel.register({
                    experiment_branch: process.env.RENDER_GIT_BRANCH,
                });
            };

            model.updateSentry = function () {
                setUser({
                    id: this.sid,
                    phone: this.mobile,
                });
            };

            model.updateIterate = function () {
                Iterate("identify", {
                    email: this.email,
                    external_id: this.sid,
                });
            };

            model.updateLanguage = function () {
                let language = this.preferred_language;
                if (_.isNil(language)) {
                    language = ENGLISH;
                }
                $translate.use(language);
                amMoment.changeLocale(language + "-us");
            };

            model.generateLoginLink = function (path) {
                mixpanel.track("Generated login link from employee profile");
                let queryParams = {};
                if (path) {
                    queryParams.path = path;
                }
                return this.customGET("generateloginlink", queryParams);
            };

            model.sendLoginLink = function () {
                mixpanel.track("Sent login link from employee profile");
                return this.customGET("sendloginlink");
            };

            model.getIntercomJwt = function () {
                return this.customGET("intercom-jwt");
            };

            return model;
        });
    }

    updatePermissions(growerManagements, growerMemberships) {
        let ctrl = this;
        ctrl.PermPermissionStore.definePermission(
            AUTHENTICATED_PERMISSION,
            function () {
                return ctrl.AuthService.getAuthenticatedUserSid();
            },
        );
        ctrl.PermPermissionStore.definePermission(
            ACTIVE_GROWER_ADMIN,
            function (permissionName, transitionProperties) {
                let growerSid = _.get(
                    transitionProperties,
                    "toParams.grower_sid",
                );
                let growerManagement = _.find(growerManagements, [
                    "grower_organization",
                    growerSid,
                ]);
                return !!growerManagement && growerManagement.active !== false;
            },
        );
        ctrl.PermPermissionStore.defineManyPermissions(
            PERMISSION_NAMES,
            function (permissionName, transitionProperties) {
                let toStateName = transitionProperties.toState.name;
                let toParams = transitionProperties.toParams;
                let growerManagement = _.find(growerManagements, [
                    "grower_organization",
                    toParams.grower_sid,
                ]);
                let growerManagementPermissions = _.get(
                    growerManagement,
                    "permissions",
                    [],
                );
                return _.includes(
                    _.map(growerManagementPermissions, "value"),
                    permissionName,
                );
            },
        );
    }

    invite(inviteUser) {
        return this.Restangular.all(this.resource).customPOST(
            inviteUser,
            "invite",
        );
    }

    signUp(params) {
        return this.Restangular.all(this.resource).customPOST(params, "signup");
    }
}

export class GrowerOrganizationService extends ResourceService {
    /* @ngInject */
    constructor(
        Restangular,
        $q,
        GrowerMembershipService,
        AccountService,
        GrowerManagementMembershipService,
    ) {
        super(Restangular, $q, "growerorganizations");
        this.GrowerMembershipService = GrowerMembershipService;
        this.AccountService = AccountService;
        this.GrowerManagementMembershipService =
            GrowerManagementMembershipService;

        let svc = this;
        this.Restangular.extendModel(this.resource, function (model) {
            model.addManager = function (user) {
                return svc.GrowerManagementMembershipService.create({
                    user: user,
                    grower_organization: this.sid,
                    active: true,
                });
            };

            model.switchToAutoPay = function () {
                this.stripe_collection_method = CHARGE_AUTOMATICALLY;
                return this.save();
            };

            model.getBillingStatus = function () {
                return this.customGET("billing/status");
            };

            model.getInvoices = function () {
                return this.customGET("billing/invoices");
            };

            model.getCreditBalance = function () {
                return this.customGET("billing/credit_balance");
            };

            model.getHistoricalBillingUsage = function () {
                return this.customGET("billing/historical_usage", {
                    start_date: moment().subtract(365, "days").toISOString(),
                    end_date: moment().toISOString(),
                });
            };

            model.getDefaultPaymentMethod = function () {
                return this.customGET("billing/default_payment_method");
            };

            model.getUpdatePaymentMethodSecret = function () {
                return this.customPOST({}, "billing/update_payment_method");
            };

            model.getRequiresActionSetupIntents = function () {
                return this.customGET("billing/requires_action_setup_intents");
            };

            model.getCreditBonusCalculation = function (amount) {
                return this.customGET("billing/calculate_bonus", { amount });
            };

            model.addCredit = function (amount) {
                return this.customPOST({ amount }, "billing/add_credit");
            };

            model.setupEnrichment = function (payload) {
                return this.customPOST({ payload }, "setup-enrichment");
            };

            model.downloadInvoiceUsage = function (stripe_invoice_id) {
                return this.customPOST(
                    { stripe_invoice_id },
                    "billing/invoice_usage_export",
                );
            };

            return model;
        });
    }
}

export class MessageService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "messages");
    }
}
export class AttachmentService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "attachments");
    }
}
export class AnnouncementTRANSLATEDService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "announcementtranslateds");
    }
}
export class AnnouncementService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http, DownloadService, $translate) {
        super(Restangular, $q, "announcements");
        this.$http = $http;
        this.DownloadService = DownloadService;
        this.$translate = $translate;

        let svc = this;
        svc.Restangular.extendModel(this.resource, function (model) {
            model.send = function () {
                return this.customPOST(undefined, "send");
            };

            model.print = function () {
                return this.customGET("print");
            };

            model.download = function () {
                return this.customGET("download");
            };

            model.delete = function () {
                this.active = false;
                return this.save();
            };

            return model;
        });
    }
}
export class ConversationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "conversations");

        let svc = this;
        svc.Restangular.extendModel(this.resource, function (model) {
            model.download = function () {
                return this.customGET("download");
            };
            model.close = function () {
                this.state = CLOSED;
                return this.save();
            };

            model.addParticipant = function () {
                return this.customPUT(undefined, "add_participant");
            };

            return model;
        });
    }

    getToken() {
        let svc = this;
        return svc.Restangular.all(this.resource).customGET("token");
    }

    downloadResubscribePoster(growerOrganizationSid) {
        let svc = this;
        return svc.Restangular.all(this.resource).customGET(
            "download_resubscribe_poster",
            { grower_organization: growerOrganizationSid },
        );
    }
}
export class AlertThemeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "alertthemes");
    }
}
export class AlertSubscriptionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "alertsubscriptions");
        let svc = this;
        svc.Restangular.extendModel(this.resource, function (model) {
            model.unsubscribe = function () {
                this.active = false;
                this.end_date = moment();
                return this.save();
            };

            return model;
        });
    }
}
export class AccidentReportService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "accidentreports");

        this.Restangular.extendModel(this.resource, function (model) {
            model.prefill = function () {
                return this.customGET("prefill").then(
                    (response) => response.data,
                );
            };

            model.download = function (language) {
                return this.customGET("download", { language });
            };

            model.archive = function () {
                this.archived = true;
                return this.save();
            };

            model.getReportFileName = function () {
                return (
                    "Accident Report OSHA 301 " +
                    moment(this.accident_date).format(UNDERSCORE_DATE_FORMAT)
                );
            };

            model.getMissingFieldCount = function () {
                return ACCIDENT_REPORT_SOFT_REQUIRED_FIELDS.filter(
                    (field) => !this[field],
                ).length;
            };

            return model;
        });
    }

    downloadLog(farm, year) {
        let ctrl = this;
        return ctrl.Restangular.all(ctrl.resource).customPOST(
            { farm: farm, year: year },
            "download_log",
        );
    }

    downloadLogAndSummary(data) {
        let ctrl = this;
        return ctrl.Restangular.all(ctrl.resource).customPOST(
            data,
            "download_log_summary",
        );
    }
}

export class AccidentReportTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "accidentreporttranslateds");
    }
}

export class AccidentReportAttachmentService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "accidentreportattachments");
    }
}

export class CommentService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $translate) {
        super(Restangular, $q, "safetycomments");
        this.$translate = $translate;
    }

    summarize(comments) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            {
                comments: _.map(comments, SID_LOOKUP),
                language: svc.$translate.use(),
            },
            "summarize",
        );
    }
}

export class CommentTRANSLATEDService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "safetycommentstranslateds");
    }
}

export class SignatureService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "signatures");
    }
}

export class CredentialService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "credentials");
    }
}

export class CredentialTypeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "credentialtypes");
    }
}

export class SafetyCourseService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "safetycourses");
    }
}

export class TrainingReportService extends ResourceService {
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "trainingreports");
        this.$http = $http;
        this.endpoint = process.env.API_DOMAIN + "/api/trainingreports";
    }

    options(growerSid, language) {
        return this.$http
            .get(this.endpoint + "/options", {
                params: { grower_organization: growerSid, language },
            })
            .then((response) => response.data);
    }

    evaluate(data) {
        return this.$http
            .post(this.endpoint + "/evaluate", data)
            .then((response) => response.data);
    }

    export(data) {
        return this.$http.post(this.endpoint + "/export", data);
    }

    save(sid, data) {
        if (sid) {
            return this.Restangular.one(this.resource, sid).customPUT(data);
        }
        return this.create(data);
    }

    remove(sid) {
        return this.Restangular.one(this.resource, sid).remove();
    }
}

TrainingReportService.$inject = ["Restangular", "$q", "$http"];

export class CourseMemberService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "coursemembers");
    }
}

export class AssessmentObjectiveService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "assessmentobjectives");
    }
}

export class SafetyMeetingRequestService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, SignatureService, DownloadService, $http) {
        super(Restangular, $q, "safetymeetingrequests");

        this.DownloadService = DownloadService;
        this.$http = $http;

        this.rejectionResponses = {
            403: "SafetyMeetingRequestService.LOCKED_TOAST",
            404: "SafetyMeetingRequestService.NOT_FOUND_TOAST",
        };

        this.Restangular.extendModel(this.resource, function (model) {
            model.complete = function (
                certifier,
                certificationType,
                signatureImageData = null,
            ) {
                let instance = this;
                instance.status = COMPLETE;
                instance.certifier = certifier.sid;
                instance.certification_type = certificationType;
                let signatureResponses = [];
                if (signatureImageData) {
                    let payload = {
                        signer: instance.subject,
                        image_data: signatureImageData,
                        grower_organization: instance.requester,
                    };
                    if (instance.grower_membership) {
                        payload.grower_membership = instance.grower_membership;
                    }
                    let response = SignatureService.create(payload);
                    signatureResponses.push(response);
                }
                return $q.all(signatureResponses).then(function (responses) {
                    let signature = _.first(responses);
                    if (signature) {
                        instance.signature = signature.sid;
                    }
                    instance.completion_date = moment();
                    return instance.save();
                });
            };

            model.starting = function () {
                if (this.start_date) {
                    return $q.when(this);
                }
                this.start_date = moment();
                return this.save();
            };

            model.saveProgress = function (payload) {
                return this.customPOST(payload, "progress");
            };

            model.notify = function () {
                return this.customGET("notify");
            };

            model.cancel = function () {
                this.status = CANCELLED;
                this.cancelled_date = moment();
                return this.save();
            };

            return model;
        });
    }

    handleRejection(reason) {
        let svc = this;
        return svc.rejectionResponses[reason.status];
    }

    downloadHistory(trainee, requester) {
        let ctrl = this;
        return ctrl.Restangular.all(ctrl.resource).customPOST(
            { user: trainee.sid, requester: requester.sid },
            "download_training_history",
        );
    }

    downloadHistoryData(trainees, requester, from_date, to_date) {
        let ctrl = this;
        return ctrl.Restangular.all(ctrl.resource).customPOST(
            {
                subjects: trainees,
                requester: requester.sid,
                from_date,
                to_date,
            },
            "download_training_history_data",
        );
    }

    downloadTrainingTime(options) {
        let ctrl = this;
        return ctrl.Restangular.all(ctrl.resource).customPOST(
            options,
            "download_training_time",
        );
    }

    knowledgeCheckReport(params) {
        let ctrl = this;
        return ctrl.$http
            .get(
                process.env.API_DOMAIN +
                    "/api/safetymeetingrequests/knowledge_check_report",
                { params },
            )
            .then(function (response) {
                return response.data;
            });
    }

    knowledgeCheckHistory(requestSid) {
        let ctrl = this;
        return ctrl.$http
            .get(
                process.env.API_DOMAIN +
                    "/api/safetymeetingrequests/" +
                    requestSid +
                    "/assessment_history",
            )
            .then(function (response) {
                return response.data;
            });
    }
}

export class SafetyMeetingTopicTRANSLATEDService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $sce) {
        super(Restangular, $q, "safetymeetingtopictranslateds");
        this.Restangular.extendModel(this.resource, function (model) {
            model.uploadMaterial = function (file) {
                let formData = new FormData();
                formData.append("file", file);
                return this.customPOST(formData, "upload_material", undefined, {
                    "Content-Type": undefined,
                });
            };

            model.getCFStreamUploadEndpoint = function () {
                return this.getRestangularUrl() + "/cf_stream_upload";
            };

            model.getVideoSrc = function () {
                return $sce.trustAsResourceUrl(
                    "https://customer-jxr2nkggvva4329o.cloudflarestream.com/" +
                        this.video +
                        "/iframe",
                );
            };

            return model;
        });
    }
}

export class SafetyMeetingTopicService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "safetymeetingtopics");
        this.$http = $http;

        this.Restangular.extendModel(this.resource, function (model) {
            model.getDetail = function () {
                return this.customGET("content");
            };
            return model;
        });
    }

    getAlgoliaKey() {
        return this.Restangular.all(this.resource).customGET("algolia");
    }

    getSearchClient() {
        let svc = this;
        if (svc.searchClient) {
            let deferred = svc.$q.defer();
            deferred.resolve(svc.searchClient);
            return deferred.promise;
        } else {
            return svc.getAlgoliaKey().then(function (algoliaParams) {
                svc.searchClient = searchClient(
                    process.env.ALGOLIA_APP_ID,
                    algoliaParams.public_key,
                );
                return svc.searchClient;
            });
        }
    }

    search(query, filters, additionalParams = {}) {
        let svc = this;
        return svc.getSearchClient().then(function (searchClient) {
            return svc.$q.when(
                searchClient.searchSingleIndex({
                    indexName: "safety_topic_index",
                    searchParams: {
                        filters,
                        query,
                        ...additionalParams,
                    },
                }),
            );
        });
    }
}

export class PublicTrainingService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "public-trainings");
        this.$http = $http;
    }

    getAlgoliaKey() {
        let svc = this;
        return svc.$http
            .get(
                _.join(
                    [process.env.API_DOMAIN, "api", svc.resource, "algolia"],
                    "/",
                ),
                {
                    skipAuthorization: true,
                },
            )
            .then((response) => response.data);
    }

    listPublic() {
        let svc = this;
        return svc.$http
            .get(_.join([process.env.API_DOMAIN, "api", svc.resource], "/"), {
                skipAuthorization: true,
            })
            .then((response) => response.data);
    }

    retrievePublic(sid) {
        let svc = this;
        return svc.$http
            .get(
                _.join([process.env.API_DOMAIN, "api", svc.resource, sid], "/"),
                { skipAuthorization: true },
            )
            .then((response) => response.data);
    }
}

export class SafetyMeetingService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, SignatureService) {
        super(Restangular, $q, "safetymeetings");

        this.Restangular.extendModel(this.resource, function (model) {
            model.getReport = function (includeComments) {
                return this.customGET("report", {
                    include_comments: includeComments,
                });
            };

            model.getReportFileName = function () {
                return (
                    "Safety meeting - training record " +
                    this.name +
                    " " +
                    moment(this.start_date).format(UNDERSCORE_DATE_FORMAT)
                );
            };

            model.finish = function (
                signatureImageData,
                finishUser,
                finishGrower,
                finishGrowerMembershipSid,
            ) {
                let instance = this;
                let signatureResponses = [];
                instance.scheduler = finishUser.sid;
                if (signatureImageData) {
                    let payload = {
                        signer: instance.scheduler,
                        image_data: signatureImageData,
                        grower_organization: finishGrower.sid,
                    };
                    if (finishGrowerMembershipSid) {
                        payload.grower_membership = finishGrowerMembershipSid;
                    }
                    let response = SignatureService.create(payload);
                    signatureResponses.push(response);
                }
                return $q.all(signatureResponses).then(function (responses) {
                    let signature = _.first(responses);
                    if (signature) {
                        instance.finish_signature = signature.sid;
                    }
                    instance.finish_date = moment();
                    return instance.customPUT(undefined, "finish");
                });
            };

            model.cancel = function () {
                let instance = this;
                instance.active = false;
                return instance.save();
            };

            model.sendCrewBossAttendanceLink = function (recipients) {
                return this.customPOST(
                    { recipients },
                    "send_crew_boss_attendance",
                );
            };

            model.sendOrientationReminder = function (userSid) {
                return this.customPOST(
                    {
                        user: userSid,
                    },
                    "send_orientation_reminder",
                );
            };

            model.sendRegularReminderToIncomplete = function (
                firstTime = false,
            ) {
                return this.customPOST(
                    {
                        first_time: firstTime,
                    },
                    "send_regular_reminder",
                );
            };

            model.messageIncompleteParticipants = function (data) {
                return this.customPOST(data, "send_message");
            };

            return model;
        });
    }
}

export class DocumentPackageService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http, $translate) {
        super(Restangular, $q, "documentpackages");

        this.PROCESSING = "processing";
        this.TESTING = "testing";
        this.LIVE = "live";
        this.ARCHIVED = "archived";

        this.Restangular.extendModel(this.resource, function (model) {
            model.finalizeCreation = function (translations) {
                return this.customPOST({ translations }, "finalize_creation");
            };

            model.isCustomizable = function () {
                return _.get(this.subject_fields, "requester", []).length > 0;
            };

            model.getDocumentPackageTranslated = function (
                documentPackageTranslateds,
            ) {
                let matchingDocumentTranslateds = _.filter(
                    documentPackageTranslateds,
                    ["document_package", this.sid],
                );
                let preferredTranslated = _.find(matchingDocumentTranslateds, [
                    "language",
                    $translate.use(),
                ]);
                return (
                    preferredTranslated || _.first(matchingDocumentTranslateds)
                );
            };

            model.requiresCustomization = function (documentPackageTranslated) {
                let schema = documentPackageTranslated.schema;
                return (
                    _.intersection(
                        schema.required,
                        _.get(this.subject_fields, "requester", []),
                    ).length > 0
                );
            };

            model.customizationIsValid = function (
                customizationTranslateds,
                documentPackageTranslated,
            ) {
                let instance = this;
                if (
                    customizationTranslateds.length == 0 ||
                    _.isNil(documentPackageTranslated)
                ) {
                    return false;
                }
                return _.every(customizationTranslateds, function (translated) {
                    return translated.isValid(
                        documentPackageTranslated,
                        _.get(instance, "subject_fields.requester", []),
                    );
                });
            };

            model.displayName = function (
                documentPackageTranslated,
                customization,
            ) {
                let str = documentPackageTranslated.name;
                if (customization) {
                    str += " (" + customization.name + ")";
                }
                return str;
            };

            model.formatDates = function (data) {
                let instance = this;
                _.forEach(instance.date_fields, function (options, field) {
                    let dateValue = _.get(data, field);
                    if (dateValue) {
                        data[field] = moment(dateValue).format(options.format);
                    }
                });
                return data;
            };

            model.makeDates = function (data) {
                let instance = this;
                _.forEach(instance.date_fields, function (options, field) {
                    let dateValue = _.get(data, field);
                    if (dateValue) {
                        data[field] = moment(dateValue, options.format);
                    }
                });
                return data;
            };

            model.makeDefaultDates = function (data, role, schema) {
                let instance = this;
                let fieldsToCheck = _.get(
                    instance.fields,
                    role,
                    _.flatten(_.values(instance.fields)),
                );
                _.forEach(instance.date_fields, function (options, field) {
                    let dateValue = schema
                        ? getSchemaDataValue(data, schema, field)
                        : data[field];
                    if (
                        _.includes(fieldsToCheck, field) &&
                        options.default === "now" &&
                        _.isEmpty(dateValue)
                    ) {
                        if (schema) {
                            setSchemaDataValue(
                                data,
                                schema,
                                field,
                                moment().format(options.format),
                            );
                        } else {
                            data[field] = moment().format(options.format);
                        }
                    }
                });
                return data;
            };

            return model;
        });
    }

    getAlgoliaKey() {
        return this.Restangular.all(this.resource).customGET("algolia");
    }

    getSearchClient() {
        let svc = this;
        if (svc.searchClient) {
            let deferred = svc.$q.defer();
            deferred.resolve(svc.searchClient);
            return deferred.promise;
        } else {
            return svc.getAlgoliaKey().then(function (algoliaParams) {
                svc.searchClient = searchClient(
                    process.env.ALGOLIA_APP_ID,
                    algoliaParams.public_key,
                );
                return svc.searchClient;
            });
        }
    }

    search(query, filters, additionalParams = {}) {
        let svc = this;
        return svc.getSearchClient().then(function (searchClient) {
            return svc.$q.when(
                searchClient.searchSingleIndex({
                    indexName: "document_package_index",
                    searchParams: {
                        filters,
                        query,
                        ...additionalParams,
                    },
                }),
            );
        });
    }
}

export class DocumentPackageTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "documentpackagetranslateds");
        this.$http = $http;

        this.Restangular.extendModel(this.resource, function (model) {
            model.getPageDimensions = function (pageIndex) {
                let instance = this;
                const template_specification = instance.template_specification;
                if (template_specification.template_type === "html") {
                    pageIndex = -1;
                }
                return instance.template_specification.page_dimensions[
                    pageIndex
                ];
            };

            model.getSignatureFieldNames = function () {
                return getSignatureFieldNames(this);
            };

            model.createTemplate = function (externalFileId) {
                let data = {
                    external_file_id: externalFileId,
                };
                return this.customPOST(data, "create_template");
            };

            return model;
        });
    }

    renderPreview(sid) {
        let svc = this;
        return svc.Restangular.one(svc.resource, sid).customPOST(
            {},
            "render_preview",
        );
    }

    getPreviewUrl(sid, shouldCancel = () => false) {
        let svc = this;
        return svc.renderPreview(sid).then(function (data) {
            return svc.$q.when(
                promisePoller({
                    retries: 300,
                    interval: 300,
                    taskFn: function () {
                        if (shouldCancel()) {
                            return svc.$q.reject(CANCEL_TOKEN);
                        }
                        let testUrl = getCloudinaryUrl(
                            "v" + moment().valueOf() + "/" + data.public_id,
                        );
                        return svc.$http.head(testUrl).then(function () {
                            return getCloudinaryUrl("v1/" + data.public_id);
                        });
                    },
                }),
            );
        });
    }
}

export class DocumentPackageRequestService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "documentpackagerequests");

        this.Restangular.extendModel(this.resource, function (model) {
            model.cancel = function () {
                this.cancelled = true;
                this.cancelled_date = moment();
                return this.save();
            };

            model.sign = function (formData, signerSid, initiatorSid) {
                let data = {
                    form_data: formData,
                    signer: signerSid,
                    initiator: initiatorSid,
                };
                return this.customPOST(data, "sign");
            };

            model.edit = function (formData, initiatorSid) {
                let data = {
                    form_data: formData,
                    initiator: initiatorSid,
                };
                return this.customPOST(data, "edit");
            };

            model.notify = function () {
                return this.customGET("notify");
            };

            model.promptExternal = function () {
                return this.customGET("prompt_external");
            };

            model.renderPreview = function (data) {
                return this.customPOST({ data }, "render_preview");
            };

            model.updateData = function (data) {
                return this.customPOST({ data }, "update_data");
            };

            model.isInPrefill = function () {
                return (
                    this.initiator_fill_first && this.latest_signer_order === -1
                );
            };

            model.isReadyToSendForSigning = function (
                documentPackage,
                role,
                documentPackageTranslated,
                validationData,
            ) {
                let instance = this;
                let schema = buildRoleScopedFormSchema(
                    documentPackageTranslated,
                    documentPackage,
                    instance,
                    role,
                );
                const schemaObj = compileSchema(schema, {
                    drafts: [draft07],
                });
                let data = validationData || instance.data || {};
                const roleScopedData = getDocumentFieldPathData(data, schema);
                const prunedData = getReactSchemaFormData(
                    _.pickBy(roleScopedData, function (value) {
                        return !_.isNil(value);
                    }),
                    schema,
                );
                const { valid, errors } = schemaObj.validate(prunedData);
                return valid;
            };

            return model;
        });
    }

    prefillRequest(requestSid, currentInitiatorSid) {
        let svc = this;
        let payload;
        if (currentInitiatorSid) {
            payload = { current_initiator: currentInitiatorSid };
        } else {
            payload = {};
        }
        return svc.Restangular.one(svc.resource, requestSid)
            .customPOST(payload, "prefill")
            .then((response) => response.data);
    }

    createRequestsFromPacket(data) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            data,
            "create_from_packet",
        );
    }

    notify(data) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            data,
            "notify_generic",
        );
    }
}

export class DocumentPackageRequestPrefillAttemptService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "documentpackagerequestprefillattempts");
    }

    realtimeRole(data = {}) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            data,
            "realtime_role",
        );
    }
}

export class DocumentPackageRecordService extends ResourceService {
    /* @ngInject */
    constructor(
        Restangular,
        $q,
        $window,
        ToastService,
        $http,
        PollDownloadService,
    ) {
        super(Restangular, $q, "documentpackagerecords");
        this.ToastService = ToastService;
        this.$http = $http;
        this.PollDownloadService = PollDownloadService;
        let svc = this;

        this.Restangular.extendModel(this.resource, function (model) {
            model.archive = function () {
                mixpanel.track("Archived document");
                this.archived = true;
                return this.save();
            };

            model.download = function (newTab = false) {
                return this.customGET("download").then(function (response) {
                    return svc.PollDownloadService.get(response).then(
                        function (url) {
                            downloadFile(url, newTab);
                        },
                        function (reasons) {
                            svc.ToastService.create(
                                "DocumentPackageRecordService.POLL_FAILED",
                            );
                            captureException(
                                "DocumentPackageRecord data export failed",
                                {
                                    contexts: {
                                        error: {
                                            attempts: reasons.length,
                                            last_attempt: _.last(reasons),
                                        },
                                    },
                                },
                            );
                        },
                    );
                });
            };

            return model;
        });
    }

    downloadBulk(options) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            options,
            "download_bulk",
        );
    }

    dataDownload(options) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            options,
            "data_download",
        );
    }
}

export class PacketService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "packets");
    }
}

export class PacketMemberService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "packetmembers");
    }
}

export class SignatureRequestService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, AuditTrailEventService) {
        super(Restangular, $q, "signaturerequests");

        this.Restangular.extendModel(this.resource, function (model) {
            model.updateDataRequest = function () {
                return this.customGET("updatedatarequest");
            };

            model.getAuthToken = function () {
                return this.customGET("authtoken");
            };

            model.refresh = function () {
                return this.customGET("refresh");
            };

            model.notify = function () {
                return this.customGET("notify");
            };

            model.addAuditTrailEvent = function (event, context = {}) {
                let payload = {
                    date: moment(),
                    event: event,
                    user: this.signer,
                    document_package_request: this.document_package_request,
                    signature_request: this.sid,
                    context: context,
                    grower_membership: this.grower_membership,
                };
                return AuditTrailEventService.create(payload).catch((e) => {});
            };

            model.addAuditTrailEventOLD = function (event) {
                let data = {
                    date: moment(),
                    event: event,
                };
                return this.customPOST(data, "add_audit_trail_event").catch(
                    (e) => {},
                );
            };

            return model;
        });
    }
}

export class AuditTrailEventService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $translate, $window) {
        super(Restangular, $q, "audittrailevents");
        this.$translate = $translate;
        this.$window = $window;
        this.currentIPAddress;
    }

    determineIPAddress() {
        let svc = this;
        return publicIpv4().then(
            function (ipAddress) {
                svc.currentIPAddress = ipAddress;
            },
            function (error) {
                svc.currentIPAddress = null;
            },
        );
    }

    create(data) {
        let svc = this;
        data.context.language = svc.$translate.use();
        data.ip_address = svc.currentIPAddress;
        data.user_agent = svc.$window.navigator.userAgent;
        return super.create(data);
    }
}

export class IdentificationDocumentService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "identificationdocuments");

        this.Restangular.extendModel(this.resource, function (model) {
            model.archive = function () {
                this.archived = true;
                return this.save();
            };

            model.getDataForDocumentPackage = function (documentPackage) {
                return this.customGET("get_data_for_document_package", {
                    document_package: documentPackage.sid,
                });
            };

            return model;
        });
    }

    sendDocumentScanLink(params) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            params,
            "sendscanlink",
        );
    }
}

export class IdentificationDocumentTypeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "identificationdocumenttypes");
    }

    determine(params) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            params,
            "determine",
        );
    }
}

export class OnboardingConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "onboardingconfigurations");
    }
}

export class OnboardingConfigurationTRANSLATEDService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "onboardingconfigurationtranslateds");
    }
}

export class SafetyMeetingProgramService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "safetymeetingprograms");
    }
}

export class SafetyMeetingProgramElementService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "safetymeetingprogramelements");
    }
}

export class SafetyMeetingProgramSubscriptionService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "safetymeetingprogramsubscriptions");

        let ctrl = this;
        ctrl.Restangular.extendModel(this.resource, function (model) {
            model.unsubscribe = function () {
                this.active = false;
                this.end_date = moment();
                return this.save();
            };

            return model;
        });
    }
}

export class TimeTrackingVendorService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "timetrackingvendors");
        let svc = this;
        svc.Restangular.extendModel(this.resource, function (model) {
            model.isLinkable = function () {
                return _.includes(this.capabilities, "link");
            };

            model.link = function (grower, link_code) {
                return this.customPOST(
                    {
                        grower_organization: grower.sid,
                        link_code,
                        time_tracking_vendor: this.sid,
                    },
                    "link",
                );
            };

            return model;
        });
    }
}

export class EmployeeBadgeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "employeebadges");
        let svc = this;
        svc.Restangular.extendModel(svc.resource, function (model) {
            model.getBadge = function () {
                return this.customGET("download");
            };
            return model;
        });
    }
}

export class TimeTrackingVendorConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "timetrackingvendorconfigurations");

        let FIELDCLOCK_COLOR = "bg-[#23c6c8]";
        let T3_COLOR = "bg-[#17405e]";
        let ROY_FARMS_COLOR = "bg-[#90C400]";
        let DOUGLAS_FRUIT_COLOR = "bg-[#f15a29]";
        let PAGO_COLOR = "bg-[#258758]";
        let TIME_PORTAL_COLOR = "bg-[#1694fb]";
        let AGSQUARED_COLOR = "bg-[#4d7e11]";
        let AGRITRAK_COLOR = "bg-[#576240]";
        let SECOND_SIGHT_BIO_COLOR = "bg-[#6cc24a]";

        let ctrl = this;
        ctrl.Restangular.extendModel(this.resource, function (model) {
            model.search = function (queryParams) {
                return this.customGET("search", queryParams);
            };

            model.create = function (growerMembershipSid) {
                return this.customPOST(
                    { grower_membership: growerMembershipSid },
                    "create_time_tracking_employee",
                );
            };

            model.getBadge = function (growerMembershipSid) {
                return this.customGET("badge", {
                    grower_membership: growerMembershipSid,
                });
            };

            return model;
        });
    }

    find_grower_membership(value, growerSid) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customGET(
            "find_grower_membership",
            { value: value, grower_organization: growerSid },
        );
    }

    bulkBadgeDownload(growerMembershipSids, growerSid) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            {
                grower_memberships: growerMembershipSids,
                grower_organization: growerSid,
            },
            "bulk_badge_download",
        );
    }
}

export class TimeTrackingEmployeeConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "timetrackingemployeeconfigurations");
    }
}

export class AccidentReportNoteService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "accidentreportnotes");
    }
}

export class AccidentReportNoteTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "accidentreportnotetranslateds");
    }
}

export class AdvisorConversationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "advisorconversations");
    }

    getToken() {
        let svc = this;
        return svc.Restangular.all(this.resource).customGET("token");
    }

    createTwilioClient() {
        let svc = this;
        return svc.getToken().then(function (data) {
            let deferred = svc.$q.defer();
            const client = new ConversationsClient(data.token);
            client.on("tokenAboutToExpire", function () {
                addTwilioConversationsBreadcrumb(
                    "AdvisorConversationService client tokenAboutToExpire",
                );
                svc.getToken().then(function (data) {
                    client.updateToken(data.token);
                });
            });
            client.on("tokenExpired", function () {
                addTwilioConversationsBreadcrumb(
                    "AdvisorConversationService client tokenExpired",
                );
                svc.getToken().then(function (data) {
                    client.updateToken(data.token);
                });
            });
            client.on("initialized", function () {
                addTwilioConversationsBreadcrumb(
                    "AdvisorConversationService client initialized",
                );
                deferred.resolve(client);
            });
            client.on("initFailed", function (error) {
                addTwilioConversationsBreadcrumb(
                    "AdvisorConversationService client initFailed",
                );
                deferred.reject(error);
            });
            return deferred.promise;
        });
    }
}

export class AdvisorSnippetService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "advisorsnippets");
        this.$http = $http;

        let ctrl = this;
        ctrl.Restangular.extendModel(this.resource, function (model) {
            model.share = function (shouldIndex) {
                this.public = true;
                if (shouldIndex) {
                    this.indexable = shouldIndex;
                }
                return this.save();
            };

            return model;
        });
    }

    getPublic(sid) {
        let svc = this;
        return svc.$http
            .get(
                _.join(
                    [
                        process.env.API_DOMAIN,
                        "api",
                        svc.resource,
                        sid,
                        "public",
                    ],
                    "/",
                ),
                { skipAuthorization: true },
            )
            .then((response) => response.data);
    }
}

export class AdvisorMessageService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "advisormessages");
    }
}

export class H2ACandidateService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "h2acandidates");
    }
}

export class HousingSiteService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "housingsites");
    }
}

export class HousingAssignmentService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "housingassignments");

        this.Restangular.extendModel(this.resource, function (model) {
            model.end = function (move_out_date = moment()) {
                this.active = false;
                this.move_out_date = move_out_date;
                return this.save();
            };

            return model;
        });
    }
}

export class HousingConfigurationService extends ResourceService {
    static $inject = ["Restangular", "$q"];
    constructor(Restangular, $q) {
        super(Restangular, $q, "housingconfigurations");
    }
}

export class CommunicationConfigurationService extends ResourceService {
    static $inject = ["Restangular", "$q"];
    constructor(Restangular, $q) {
        super(Restangular, $q, "communicationconfigurations");
    }
}

export class HazComConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "hazcomconfigurations");

        this.Restangular.extendModel(this.resource, function (model) {
            model.downloadSDSPoster = function () {
                return this.customGET("download_sds_poster");
            };

            model.downloadWrittenProgram = function () {
                return this.customGET("download_written_program");
            };

            return model;
        });
    }
}

export class PesticideProductService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "pesticideproducts");
    }

    getAlgoliaKey() {
        return this.Restangular.all(this.resource).customGET("algolia");
    }

    getSearchClient() {
        let svc = this;
        if (svc.searchClient) {
            let deferred = svc.$q.defer();
            deferred.resolve(svc.searchClient);
            return deferred.promise;
        }
        return svc.getAlgoliaKey().then(function (algoliaParams) {
            svc.searchClient = searchClient(
                process.env.ALGOLIA_APP_ID,
                algoliaParams.public_key,
            );
            return svc.searchClient;
        });
    }

    search(query, filters, additionalParams = {}) {
        let svc = this;
        return svc.getSearchClient().then(function (client) {
            return svc.$q.when(
                client.searchSingleIndex({
                    indexName: "spray_pesticide_product_index",
                    searchParams: {
                        filters,
                        query,
                        ...additionalParams,
                    },
                }),
            );
        });
    }
}

export class ApplicationRecordService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "applicationrecords");

        this.Restangular.extendModel(this.resource, function (model) {
            model.getReiConversationToken = function () {
                return this.customGET("rei_conversation_token");
            };

            model.addReiConversationParticipant = function () {
                return this.customPUT(undefined, "add_participant");
            };

            model.getStatus = function () {
                let now = moment();
                let startDate = this.start_date
                    ? moment(this.start_date)
                    : null;
                let endDate = this.end_date ? moment(this.end_date) : null;
                let reiExpirationDate = this.rei_expiration_date
                    ? moment(this.rei_expiration_date)
                    : null;

                if (!startDate && !endDate) {
                    return SPRAY_APPLICATION_RECORD_STATUSES.SCHEDULED;
                }

                if (startDate && startDate.isAfter(now)) {
                    return SPRAY_APPLICATION_RECORD_STATUSES.SCHEDULED;
                }

                if (
                    startDate &&
                    startDate.isSameOrBefore(now) &&
                    (!endDate || endDate.isAfter(now))
                ) {
                    return SPRAY_APPLICATION_RECORD_STATUSES.IN_PROGRESS;
                }

                if (
                    startDate &&
                    startDate.isSameOrBefore(now) &&
                    endDate &&
                    endDate.isSameOrBefore(now) &&
                    !reiExpirationDate
                ) {
                    return SPRAY_APPLICATION_RECORD_STATUSES.NEEDS_FINALIZATION;
                }

                if (
                    startDate &&
                    startDate.isSameOrBefore(now) &&
                    endDate &&
                    endDate.isSameOrBefore(now) &&
                    reiExpirationDate
                ) {
                    return SPRAY_APPLICATION_RECORD_STATUSES.COMPLETED;
                }

                return null;
            };

            model.delete = function () {
                this.active = false;
                return this.save();
            };

            return model;
        });
    }

    generateSprayLog(payload) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            payload,
            "generate_spray_log",
        );
    }

    generateSprayLogPoster(payload) {
        let svc = this;
        return svc.Restangular.all(svc.resource).customPOST(
            payload,
            "generate_spray_log_poster",
        );
    }
}

export class PublicApplicationRecordService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "publicapplicationrecords");
        this.$http = $http;
    }

    list(params = {}) {
        let svc = this;
        return svc.$http
            .get(_.join([process.env.API_DOMAIN, "api", svc.resource], "/"), {
                params,
                skipAuthorization: true,
            })
            .then((response) =>
                _.get(response, ["data", "results"], response.data),
            );
    }

    retrieve(sid, params = {}) {
        let svc = this;
        return svc.$http
            .get(
                _.join([process.env.API_DOMAIN, "api", svc.resource, sid], "/"),
                {
                    params,
                    skipAuthorization: true,
                },
            )
            .then((response) => response.data);
    }
}

export class SetupOrderService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, $http) {
        super(Restangular, $q, "setuporders");
        this.$http = $http;
    }

    list(params = {}) {
        let svc = this;
        return svc.$http
            .get(_.join([process.env.API_DOMAIN, "api", svc.resource], "/"), {
                params,
                skipAuthorization: true,
            })
            .then((response) =>
                _.get(response, ["data", "results"], response.data),
            );
    }

    retrieve(sid, params = {}) {
        let svc = this;
        return svc.$http
            .get(
                _.join([process.env.API_DOMAIN, "api", svc.resource, sid], "/"),
                {
                    params,
                    skipAuthorization: true,
                },
            )
            .then((response) => response.data);
    }

    submit(sid, payload) {
        let svc = this;
        return svc.$http
            .post(
                _.join(
                    [
                        process.env.API_DOMAIN,
                        "api",
                        svc.resource,
                        sid,
                        "submit",
                    ],
                    "/",
                ),
                payload,
                {
                    skipAuthorization: true,
                },
            )
            .then((response) => response.data);
    }
}

export class SprayConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "sprayconfigurations");
    }
}

export class NeighborService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "neighbors");
    }
}

export class JobApplicationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "jobapplications");
    }
}

export class DocumentPackageCustomizationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "documentpackagecustomizations");
    }
}

export class DocumentPackageCustomizationTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "documentpackagecustomizationtranslateds");

        this.Restangular.extendModel(this.resource, function (model) {
            model.renderPreview = function (data) {
                return this.customPOST({ data }, "render_preview");
            };

            model.isValid = function (
                documentPackageTranslated,
                requesterFields,
            ) {
                requesterFields = requesterFields || [];
                let schema = _.cloneDeep(documentPackageTranslated.schema);
                if (!schema) {
                    return false;
                }
                let propertiesToValidate = _.pickBy(
                    schema.properties,
                    (value, key) => {
                        return (
                            requesterFields.includes(key) ||
                            key.startsWith("harvust__")
                        );
                    },
                );
                schema.properties = propertiesToValidate;
                schema.required = _.filter(
                    schema.required,
                    function (fieldName) {
                        return _.includes(_.keys(schema.properties), fieldName);
                    },
                );
                const node = compileSchema(schema, {
                    drafts: [draft07],
                });
                return node.validate(this.data).valid;
            };

            return model;
        });
    }
}

export class DisciplineReportService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "disciplinereports");

        this.Restangular.extendModel(this.resource, function (model) {
            model.download = function (language) {
                return this.customGET("download", { language });
            };

            return model;
        });
    }
}

export class DisciplineReportTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "disciplinereporttranslateds");
    }
}

export class DisciplineConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "disciplineconfigurations");
    }
}

// Terminations
export class TerminationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "terminations");
    }
}

export class TerminationTypeService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "terminationtypes");
    }
}

export class TerminationTypeTranslatedService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "terminationtypetranslateds");
    }
}

export class TerminationConfigurationService extends ResourceService {
    /* @ngInject */
    constructor(Restangular, $q) {
        super(Restangular, $q, "terminationconfigurations");
    }
}
